/**
 * useDeviceDetection Hook
 * Returns comprehensive device information and updates on window resize
 */
import { useState, useEffect } from 'react';
import {
  getDeviceInfo,
  DeviceInfo,
  getDeviceType,
  getPlatform,
  hasCoarsePointer,
  canHover,
  supportsWebSpeechAPI,
  supportsVisualViewport,
  shouldUseNativeSpeech,
  isMobileDevice,
} from '../utils/deviceDetection';

/**
 * Hook to get device information and track changes on resize
 * @returns DeviceInfo object with device details
 *
 * @example
 * const deviceInfo = useDeviceDetection();
 * if (deviceInfo.deviceType === 'phone') {
 *   // Mobile-specific logic
 * }
 */
export function useDeviceDetection(): DeviceInfo {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() => {
    if (typeof window === 'undefined') {
      return {
        platform: 'unknown',
        deviceType: 'desktop',
        isMobile: false,
        isTouch: false,
        canHover: false,
        supportsWebSpeech: false,
        supportsVisualViewport: false,
        shouldUseNativeSpeech: false,
      };
    }
    return getDeviceInfo();
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Initial update to ensure correct state
    setDeviceInfo(getDeviceInfo());

    // Create resize handler to update on device orientation/window size changes
    const handleResize = () => {
      setDeviceInfo(getDeviceInfo());
    };

    // Add resize listener
    window.addEventListener('resize', handleResize);

    // Also listen for orientation change on mobile devices
    window.addEventListener('orientationchange', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return deviceInfo;
}
